// DocTree.tsx — the recorded documentation of one repository, as the tree it
// actually is. Directories collapse, every node states its TL;DR beside its
// name, and selecting one hands it to the page, which renders the document
// alongside.
//
// A tree rather than a list because the documentation *is* the source layout:
// a directory's summary is assembled from its children's, so reading it makes
// sense only with the children in view underneath it. The TL;DR on each row is
// what makes the tree worth walking at all — a directory listing tells you the
// names, which you already knew from the repository; the one-line summary is
// the thing docdaemon added.
//
// Node open/closed state lives in Expando, one instance per node, so nesting
// costs the page no bookkeeping and collapsing a directory never disturbs a
// sibling. Row chrome (link-variant chevron, indent by depth) matches the run
// table and imprinter's module tree, so the estate's three trees read alike.
import { Fragment } from 'react';
import Button from 'react-bootstrap/Button';
import { BsFileEarmarkText, BsFolder2, BsFolder2Open } from 'react-icons/bs';
import Expando, { ExpandoChevron } from 'common/ui/Expando';
import type { DocNode } from '../api';

export default function DocTree({ tree, selected, onSelect, roomy = false }: {
  tree: DocNode;
  // The repository root's path is the empty string, so "nothing selected" is
  // null rather than '' — otherwise the root row highlights itself on a
  // repository where nothing has been documented yet.
  selected: string | null;
  onSelect: (node: DocNode) => void;
  // roomy is the full-width reading mode: larger type, taller rows, and a
  // deeper indent per level. The indent has to grow with the type or the
  // nesting stops being legible at the size that was supposed to make it more
  // legible — a step that reads as a step at 0.875rem is nearly flush at
  // 1.05rem.
  roomy?: boolean;
}) {
  return (
    <div className={roomy ? 'doc-tree doc-tree-roomy' : 'doc-tree'}>
      <TreeNode
        node={tree}
        depth={0}
        selected={selected}
        onSelect={onSelect}
        indent={roomy ? 1.5 : 1.1}
      />
    </div>
  );
}

// childOrder is directories first, then files, each alphabetical — the reading
// order every file browser has trained people to expect. It is applied here
// rather than trusted from the server because the tree is assembled from a
// filesystem walk, and a walk's order is an implementation detail of the
// walker.
function childOrder(a: DocNode, b: DocNode): number {
  if (a.kind !== b.kind) return a.kind === 'dir' ? -1 : 1;
  return a.name.localeCompare(b.name);
}

function TreeNode({ node, depth, selected, onSelect, indent }: {
  node: DocNode;
  depth: number;
  selected: string | null;
  onSelect: (node: DocNode) => void;
  indent: number;
}) {
  if (node.kind === 'file') {
    return <NodeRow node={node} depth={depth} selected={selected} onSelect={onSelect} indent={indent} />;
  }
  const children = [...(node.children ?? [])].sort(childOrder);
  return (
    // The repository root opens by default and nothing else does: a repo of
    // any size has more directories than fit on a screen, so expanding
    // everything would bury the top level the reader starts from.
    <Expando defaultOpen={depth === 0}>
      {({ open, toggle }) => (
        <Fragment>
          <NodeRow
            node={node}
            depth={depth}
            selected={selected}
            onSelect={onSelect}
            indent={indent}
            open={open}
            onToggle={children.length > 0 ? toggle : undefined}
          />
          {open && children.map((child) => (
            <TreeNode
              key={child.path}
              node={child}
              depth={depth + 1}
              selected={selected}
              onSelect={onSelect}
              indent={indent}
            />
          ))}
        </Fragment>
      )}
    </Expando>
  );
}

// NodeRow is one line of the tree. `onToggle` is absent for files and for empty
// directories, and the chevron's slot stays occupied anyway so every name in a
// level starts at the same x.
function NodeRow({ node, depth, selected, onSelect, indent, open, onToggle }: {
  node: DocNode;
  depth: number;
  selected: string | null;
  onSelect: (node: DocNode) => void;
  indent: number;
  open?: boolean;
  onToggle?: () => void;
}) {
  const isSelected = node.path === selected;
  const classes = ['doc-tree-row'];
  if (isSelected) classes.push('selected');
  return (
    <div
      className={classes.join(' ')}
      style={{ paddingLeft: `${0.25 + depth * indent}rem` }}
    >
      {/* The chevron's slot is a fixed width and is always occupied, so every
          name at a given depth starts at the same x whether or not its node
          can be opened. Swapping a glyph for a padded empty span — which is
          what this was — makes a childless directory sit a few pixels off from
          its siblings, and a tree whose names do not line up is a tree whose
          depth has to be read rather than seen. */}
      <span className="doc-tree-toggle">
        {onToggle && (
          <Button
            variant="link"
            size="sm"
            className="p-0 text-muted"
            onClick={onToggle}
            aria-expanded={open}
            aria-label={open ? 'Collapse directory' : 'Expand directory'}
          >
            <ExpandoChevron open={open ?? false} />
          </Button>
        )}
      </span>
      {/* The name is the control: clicking it renders the document. A node the
          daemon has walked but not yet summarised is shown — the structure is
          true and the gap is worth seeing — but it has nothing to open, so the
          button says why rather than going nowhere. */}
      <Button
        variant="link"
        size="sm"
        className={`doc-tree-name p-0 text-decoration-none text-start flex-shrink-0${isSelected ? ' fw-semibold' : ''}`}
        onClick={() => onSelect(node)}
        disabled={!node.has_doc}
        title={node.has_doc ? node.path || 'repository root' : 'No summary generated for this path yet'}
      >
        <span className="d-inline-flex align-items-center gap-2">
          {node.kind === 'dir'
            ? (open ? <BsFolder2Open /> : <BsFolder2 />)
            : <BsFileEarmarkText />}
          {node.name || '/'}
        </span>
      </Button>
      {/* The summary shares the row and truncates: the tree is read down the
          names, and a TL;DR that wraps to three lines per entry pushes the
          next directory off the screen. The full text is one click away. */}
      {node.tldr && (
        <span className="doc-tree-tldr small text-muted" title={node.tldr}>{node.tldr}</span>
      )}
    </div>
  );
}
